import React, { FormEvent, useEffect, useState } from 'react';
import FieldSelectPmsProvider from './field-select-pms-provider';
import {
    Button,
    ControlLabel,
    FormControl,
    FormGroup,
    Row,
} from 'react-bootstrap';
import FieldVatIdAndExemptionReason, {
    VatExemptionReason,
} from '../../realms/field-vat-id-and-exemption-reason';
import FieldContractualType from '../../realms/field-contractual-type';
import FieldSelectTenant from '../../realms/field-select-tenant';
import { useFormikContext } from 'formik';
import {
    ContractDetails,
    ContractDetailsUpdateDto,
    CONTRACTUAL_TYPE_LIST,
    ContractualType,
    isContractualTypePms,
    isContractualTypeWhiteLabel,
    ResetUserDetailsUpdates,
} from '../../../store/userDetailsSlice';
import useTranslate from '../../useTranslate';
import ValidatorFormGroup from '../../main/validator-form-group';
import { VAT_EXEMPTION_REASONS } from '../../../util/enums';
import { usePbDispatch, usePbSelector } from '../../../store/pbStoreHooks';
import { Country } from '../../../store/countriesDetailsSlice';
import {
    FALLBACK_DEFAULT_VALUES,
    SupportedTenantsDtoList,
} from '../../../store/accountingAreaDefaultsSlice';
import { getCountryName } from '../../../util/country-display';
import { RequestStatus } from '../../../store/network/managedRequest';
import { toPbValidationErrors } from '../../../store/network/requestErrors';
import ConfirmModal from '../../util/confirm-modal';
import { PbGenericRequest } from '../../../store/pbRequestSlice';
import PbCardErrorElement from '../../main/pb-card-error-element';

const VALIDATORS = {
    contractual_wewash_share: {
        isValid: (val) => /^\s*\d([.,]\d*)?$/.test(String(val)),
    },
    vat_exemption_reason: {
        isValid: (vat_exempted, reason) => !vat_exempted || !!reason,
    },
    vat_id: {
        isValid(vat_exempted, vat_id, tax_number) {
            if (vat_exempted && !vat_id) {
                return true;
            }
            if (tax_number != null && tax_number.length > 0) {
                return true;
            } else {
                return /(^[A-Z]{2}.{2,25}$)/.test(vat_id ?? '');
            }
        },
    },
    tax_number: {
        isValid(vat_exempted, vat_id, tax_number) {
            if (vat_exempted && !tax_number) {
                return true;
            }
            if (vat_id != null && vat_id.length > 0) {
                return true;
            } else {
                return /(^.{2,25}$)/.test(tax_number ?? '');
            }
        },
    },
    contractual_type: {
        isValid(contractual_type) {
            return CONTRACTUAL_TYPE_LIST.includes(contractual_type);
        },
    },
    country_id: {
        isValid(country_id) {
            return !!country_id;
        },
    },
    pms_provider: {
        isValid(pms_provider, contractual_type) {
            return !isContractualTypePms(contractual_type) || pms_provider;
        },
    },
    tenant_id: {
        isValid(tenant_id, contractual_type) {
            return !isContractualTypeWhiteLabel(contractual_type) || tenant_id;
        },
    },
};

export function validateContractDetails(data: ContractDetails) {
    const errors = {};

    Object.keys(VALIDATORS).forEach((it) => {
        const fieldName = it as keyof ContractDetails;
        if (!validateField(fieldName)) {
            errors[fieldName] = true;
        }
    });

    return errors;

    function validateField(fieldName: keyof ContractDetails) {
        if (fieldName === 'vat_id' || fieldName === 'tax_number') {
            return VALIDATORS[fieldName].isValid(
                data.vat_exempted,
                data.vat_id,
                data.tax_number
            );
        }
        if (fieldName === 'pms_provider' || fieldName === 'tenant_id') {
            return VALIDATORS[fieldName].isValid(
                data[fieldName],
                data.contractual_type
            );
        }
        if (fieldName === 'vat_exemption_reason') {
            return VALIDATORS[fieldName].isValid(
                data.vat_exempted,
                data.vat_exemption_reason
            );
        }
        return VALIDATORS[fieldName]?.isValid(data[fieldName]) ?? true;
    }
}

interface Props {
    disabled?: boolean;
    isAdmin?: boolean;
    mayChangeCountry?: boolean;
    submitRequest?: PbGenericRequest;
}

function ProfileContractDetailsForm(props: Readonly<Props>) {
    const dispatch = usePbDispatch();
    const countries = usePbSelector(
        (state) => state.countriesDetails.idToCountry
    );
    const accountingAreaDefaults = usePbSelector(
        (state) =>
            state.accountingAreaDefaults.accountingAreaDefaults ?? {
                contractual_type_to_values: {},
                available_pms_providers: [],
            }
    );
    const supportedTenants = usePbSelector(
        (state) => state.accountingAreaDefaults.supportedTenants
    );

    const strings = useTranslate('ProfileContractDetailsForm');
    const formProps = useFormikContext<ContractDetailsUpdateDto>();
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    useEffect(() => {
        if (props.submitRequest?.status === RequestStatus.FAILURE) {
            if (
                toPbValidationErrors(props.submitRequest?.errors).has(
                    'CONTRACTUAL_TYPE_MISMATCH'
                )
            ) {
                setShowConfirmModal(true);
            }
        }
    }, [props.submitRequest?.status, props.submitRequest?.errors]);

    const isVatExempted = formProps.values.vat_exempted;
    const countryVat = _getCountryVat(formProps.values.country_id, countries);

    if (!props.isAdmin) {
        return renderBasicPart();
    }

    return (
        <div>
            <ConfirmModal
                show={showConfirmModal}
                onConfirm={handleModalConfirm}
                onClose={handleModalClose}
                confirmTitle={strings.confirmContractualChangeTitle}
                confirmMessage={strings.confirmContractualChangeText}
                confirmAction={strings.confirmContractualChangeContinue}
                rejectAction={strings.confirmContractualChangeCancel}
            />
            {renderBasicPart()}
            {!props.disabled && (
                <Row>
                    <Button
                        type="button"
                        className="btn btn-outline-secondary pull-right"
                        onClick={() => _resetToDefaults()}
                    >
                        {strings.setDefaults}
                    </Button>
                </Row>
            )}

            <ValidatorFormGroup
                label={strings.contractual_wewash_share}
                hideErrorLabel
                errorLabel=""
            >
                <FormControl
                    type="text"
                    className={`input-text-color`}
                    value={formProps.values.contractual_wewash_share}
                    name="contractual_wewash_share"
                    onChange={handleChange('contractual_wewash_share')}
                    required
                    disabled={props.disabled}
                />
                {isVatExempted && (
                    <div>
                        {strings.calculatedShare}{' '}
                        {calculateVatAdjustedShare(
                            formProps.values.contractual_wewash_share,
                            countryVat
                        )}
                    </div>
                )}
            </ValidatorFormGroup>
        </div>
    );

    function renderAccountingAreaType() {
        const isAdmin = props.isAdmin;

        const hasOptions = !props.disabled && isAdmin;
        const fieldName = 'contractual_type';
        const isWhiteLabel = isContractualTypeWhiteLabel(
            formProps.values.contractual_type
        );
        const isPmsContract = isContractualTypePms(
            formProps.values.contractual_type
        );

        return (
            <div>
                <FieldContractualType
                    label={strings.contractual_type}
                    disabled={!hasOptions}
                    onChange={handleContractualTypeChange}
                    value={formProps.values[fieldName]}
                />
                {isWhiteLabel && (
                    <FieldSelectTenant
                        disabled={props.disabled}
                        label={strings.tenant}
                        supportedTenants={supportedTenants}
                        onChange={handleChange('tenant_id')}
                        value={formProps.values.tenant_id}
                    />
                )}
                {isPmsContract && (
                    <FieldSelectPmsProvider
                        disabled={props.disabled}
                        label={strings.pmsProvider}
                        availableRmsProviders={
                            accountingAreaDefaults.available_pms_providers
                        }
                        value={formProps.values.pms_provider}
                        onChange={handleChange('pms_provider')}
                    />
                )}
            </div>
        );
    }

    function renderBasicPart() {
        const countryChoiceDisabled = props.disabled || !props.mayChangeCountry;

        return (
            <div>
                {renderAccountingAreaType()}
                <ValidatorFormGroup
                    label={strings.country_id}
                    hideErrorLabel={shouldHideErrorForField('country_id')}
                    errorLabel={strings.country_idError}
                >
                    <select
                        disabled={countryChoiceDisabled}
                        className="form-control"
                        name="country_id"
                        onChange={handleChange('country_id')}
                        value={formProps.values.country_id}
                        required
                    >
                        {Object.values(countries).map((it) => (
                            <option key={it.id} value={it.id}>
                                {getCountryName(it, strings._currentLanguage)}
                            </option>
                        ))}
                    </select>
                </ValidatorFormGroup>
                <FormGroup>
                    <ControlLabel>
                        {strings.company_registration_number}
                    </ControlLabel>
                    <FormControl
                        type="text"
                        className={'input-text-color'}
                        value={formProps.values.company_registration_number}
                        name="company_registration_number"
                        onChange={handleChange('company_registration_number')}
                        disabled={props.disabled}
                    />
                </FormGroup>
                <FieldVatIdAndExemptionReason
                    vatExempt={formProps.values.vat_exempted}
                    vatId={formProps.values.vat_id}
                    vatIdVerificationStatus={
                        formProps.values.vat_id_verification_status
                    }
                    vatIdVerificationErrorType={
                        formProps.values.vat_id_verification_error_type
                    }
                    vatIdVerificationError={
                        formProps.values.vat_id_verification_error
                    }
                    taxNumber={formProps.values.tax_number}
                    hideVatIdError={shouldHideErrorForField('vat_id')}
                    hideTaxNumberError={shouldHideErrorForField('tax_number')}
                    vatIdDirty={formProps.touched.vat_id}
                    reason={formProps.values.vat_exemption_reason}
                    onChangeVatSetting={handleVatSettingChange}
                    onChangeVatId={handleVatIdTaxNumberChange}
                    onChangeVatIdType={handleVatIdTaxNumberChange}
                    countryCode={
                        _getCountry(formProps.values.country_id, countries)
                            ?.isocode1
                    }
                    disabled={props.disabled}
                />
                <PbCardErrorElement
                    errors={props.submitRequest?.errors}
                    hide={props.submitRequest?.status !== 'FAILURE'}
                    standardText={strings.generalError}
                />
            </div>
        );
    }

    async function handleContractualTypeChange(
        event: FormEvent<HTMLSelectElement>
    ) {
        const contractual_type = event.currentTarget.value as ContractualType;
        const pms_provider = maybeGetPmsProvider(
            formProps.values.pms_provider,
            contractual_type,
            accountingAreaDefaults.available_pms_providers
        );
        const tenant_id = maybeGetTenantId(
            formProps.values.tenant_id,
            contractual_type,
            supportedTenants
        );
        await formProps.setValues({
            ...formProps.values,
            contractual_type,
            pms_provider,
            tenant_id,
        });

        await formProps.setFieldTouched('contractual_type', true, false);
        await formProps.setFieldTouched('pms_provider', true, false);
        await formProps.setFieldTouched('tenant_id', true, false);
    }

    async function handleVatSettingChange(reason: VatExemptionReason) {
        const isVat = reason === VAT_EXEMPTION_REASONS.SUBJECT_TO_VAT;
        await formProps.setValues({
            ...formProps.values,
            vat_exempted: !isVat,
            vat_exemption_reason: isVat ? null : reason,
        });
        await formProps.setFieldTouched('vat_exemption_reason', true, false);
        await formProps.setFieldTouched('vat_exempted', true, false);
        await formProps.setFieldTouched('vat_id', true, false);
        await formProps.setFieldTouched('tax_number', true, false);
    }

    async function handleVatIdTaxNumberChange(
        vatId: string | null,
        taxNumber: string | null
    ) {
        await formProps.setFieldValue('vat_id', vatId);
        await formProps.setFieldValue('tax_number', taxNumber);
        await formProps.setFieldTouched('vat_id', true, false);
        await formProps.setFieldTouched('tax_number', true, false);
    }

    function handleChange<T>(fieldName: keyof ContractDetails) {
        return (e: FormEvent<T>) => {
            formProps.handleChange(fieldName)(e);
            formProps.setFieldTouched(fieldName, true, false).then();
        };
    }

    function shouldHideErrorForField(fieldName: keyof ContractDetails) {
        return !formProps.touched[fieldName] || !formProps.errors[fieldName];
    }

    function _resetToDefaults() {
        formProps
            .setValues({
                ...formProps.values,
                ...getDefaultsForAccountingArea(
                    formProps.values.contractual_type
                ),
            })
            .then();
        dispatch(ResetUserDetailsUpdates());
    }

    function getDefaultsForAccountingArea(contractualType: string) {
        const chosenValues =
            accountingAreaDefaults.contractual_type_to_values[contractualType];
        if (chosenValues) {
            return chosenValues;
        } else {
            return FALLBACK_DEFAULT_VALUES;
        }
    }

    async function handleModalConfirm() {
        await formProps.setFieldValue('confirm', true);
        await formProps.submitForm();
        handleModalClose();
    }

    function handleModalClose() {
        setShowConfirmModal(false);
    }
}

function _getCountryMemo(
    countryId,
    countries: { [key: number]: Country }
): Country | null {
    return countries[countryId] ?? null;
}

function _getCountry(
    countryId: number,
    countries: { [key: number]: Country }
): Country | null {
    return _getCountryMemo(countryId, countries);
}

function _getCountryVat(
    countryId: number,
    countries: { [key: number]: Country }
): number {
    const country = _getCountry(countryId, countries);
    return country?.vat_percent ?? 0;
}

function maybeGetTenantId(
    value: number | undefined | null,
    contractualType: ContractualType,
    supportedTenants?: SupportedTenantsDtoList
): number | null | undefined {
    if (!contractualType) {
        return value;
    }
    if (!isContractualTypeWhiteLabel(contractualType)) {
        return undefined;
    }
    if (value != null) {
        return value;
    }
    if ((supportedTenants?.supported_tenants?.length ?? 0) > 1) {
        return supportedTenants?.supported_tenants?.[0]?.tenant_id;
    }
    return value;
}

function maybeGetPmsProvider(
    value: string | undefined | null,
    contractualType: ContractualType,
    availableRmsProviders: string[]
): string | undefined | null {
    if (!contractualType) {
        return value;
    }
    if (!isContractualTypePms(contractualType)) {
        return undefined;
    }
    if (value != null) {
        return value;
    }
    if (availableRmsProviders.length > 0) {
        return availableRmsProviders[0];
    }
    return value;
}

function calculateVatAdjustedShare(wewashShare, vatPercent) {
    if (vatPercent === undefined || vatPercent === null) {
        return wewashShare;
    }
    const partnerShare = 1 - wewashShare;
    const vatFactor = 1 + vatPercent / 100;
    return Number(1 - partnerShare / vatFactor).toFixed(6);
}

export default ProfileContractDetailsForm;
